// utils/draftFeedback.js
//
// Draft Feedback (Ownership design v2.3, part 3; wireframe jobs-library-ownership-v3.html: Version Compare's tabs,
// the shared person's comments on the changed sentences, and the owner's comment cards in the editor). Pure
// helpers, so the tab rules, the threads and the "text that has since changed" list are tested without rendering.
// The server makes the final call (who may comment, which sentences, who resolves).

import { OUT_OF_SCOPE_MESSAGE, OUT_OF_SCOPE_REASON } from 'Utils/jobDetailsPageState';

// ── Wording (wireframe) ───────────────────────────────────────────────────────
export const COMPARE_TAB = { FEEDBACK: 'feedback', APPROVAL: 'approval', COMPARE: 'compare' };
export const COMPARE_TAB_LABEL = {
  [COMPARE_TAB.FEEDBACK]: 'Draft Feedback',
  [COMPARE_TAB.APPROVAL]: 'Approval Review',
  [COMPARE_TAB.COMPARE]: 'Version Compare',
};
export const TOOLTIP_DIFF_FEEDBACK = 'Version Compare – opens Draft Feedback, where you can comment on the changes in '
  + 'the shared draft.';
export const getFeedbackNote = (ownerName) => 'Click a highlighted change to comment on it (optional). Submit to send '
  + `your comments to ${ownerName || 'the owner'}.`;
export const SUBMIT_COMMENTS_LABEL = 'Submit comments';
export const TOOLTIP_SUBMIT_DISABLED = 'Add at least one comment first.';
export const SHARED_DRAFT_LABEL = 'Shared Draft';
export const NOT_SUBMITTED_LABEL = 'Not sent';
// The comment button on a changed sentence (collapsed threads)
export const getShowCommentsTooltip = (count) => `Show comments (${count})`;
export const HIDE_COMMENTS_TOOLTIP = 'Hide comments';
export const EDITED_LABEL = 'edited';
export const COMMENT_PLACEHOLDER = 'Comment on this change (optional)…';
export const REPLY_PLACEHOLDER = 'Your reply…';
export const EDIT_PLACEHOLDER = 'Edit your comment…';
export const MAX_COMMENT_LENGTH = 2000;   // the server's limits
export const MAX_PENDING_COMMENTS = 50;
export const TOO_MANY_COMMENTS = `Up to ${MAX_PENDING_COMMENTS} comments can be submitted at a time. Submit these `
  + 'first.';
export const INVALID_COMMENT_LABEL = 'Can\'t be submitted: this sentence is no longer a change in the draft. Remove it.';
export const ADD_COMMENT_LABEL = 'Add comment';
export const ADD_REPLY_LABEL = 'Add reply';
export const ORPHANS_TITLE = 'Comments on text that has since changed';
export const COMMENTS_PANEL_TITLE = 'Comments on this draft';
export const TOOLTIP_RESOLVE_THREAD = 'Resolve this thread (the comment and its replies)';
export const TOOLTIP_RESOLVE_COMMENT = 'Resolve this comment';
export const COMMENT_RESOLVED_TOAST = 'Comment resolved.';
export const getCommentsSentToast = (count, ownerName) => `${count} comment${count > 1 ? 's' : ''} sent to `
  + `${ownerName || 'the owner'}.`;
export const getDiscardConfirm = (count) => ({
  title: 'Discard your comments?',
  content: `You have ${count} comment${count > 1 ? 's' : ''} that ${count > 1 ? 'have' : 'has'} not been submitted. `
    + 'Close without submitting?',
  okText: 'Discard',
  cancelText: 'Keep commenting',
});
export const getDeleteConfirm = (hasReplies) => ({
  title: 'Delete this comment?',
  content: hasReplies ? 'Its replies will be deleted too.' : 'This cannot be undone.',
  okText: 'Delete',
  cancelText: 'Cancel',
});
// The owner's draft bar addition: "Karen Lee, Sam Ray has given comments on this draft – open the editor to view them."
export const getCommentsGivenText = (names) => (names?.length
  ? `${names.join(', ')} has given comments on this draft` : '');

// ── Rules ─────────────────────────────────────────────────────────────────────
// The comparison view's tabs (wireframe cmpTabs): the person a private latest draft is shared with gets Draft Feedback
// (first) and Version Compare; the assigned approver of a pending review gets Approval Review and Version Compare;
// everyone else Version Compare only.
export const getCompareTabs = ({ isSharedPerson = false, latestIsPrivateDraft = false, isApprover = false,
  isPendingApproval = false } = {}) => {
  if (isSharedPerson && latestIsPrivateDraft) return [COMPARE_TAB.FEEDBACK, COMPARE_TAB.COMPARE];
  if (isPendingApproval && isApprover) return [COMPARE_TAB.APPROVAL, COMPARE_TAB.COMPARE];
  return [COMPARE_TAB.COMPARE];
};

// A sentence or a JD in one canonical form (the backend's canonical_text): no list marker at a line's start (the
// smart-diff keeps "- " / "1. " in the sentence; dev's MDX editor writes "* " where the AI wrote "- "), links to
// their text, no emphasis, heading or URL-scheme marks, one space after . ! ? before a letter (the AI smart-diff
// joins two sentences of one line without their space: "Actions.TEST"), whitespace collapsed, lower case. So a
// sentence is found in the markdown it came from.
export const canonicalText = (text) => (typeof text === 'string' ? text : '')
  .replace(/(^|\n)[ \t]*(?:[-*\u2022+]|\d+[.)])[ \t]+/g, '$1')
  .replace(/\\\\/g, '\\').replace(/\\([&[\]])/g, '$1')
  .replace(/\[(.*?)\]\(.*?\)/g, '$1')
  .replace(/https?:\/\//g, '')
  .replace(/<[^>]+>/g, '')
  .replace(/[*_#]/g, '')
  .replace(/([.!?])(?=\p{L})/gu, '$1 ')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

const sameAnchor = (a, b) => canonicalText(a) === canonicalText(b);

// A commented sentence quoted in the editor's "changed since" list: up to `max` characters, with "…" when it is cut
// (never a sentence that just looks one letter short)
export const ANCHOR_QUOTE_MAX = 100;
export const quoteAnchor = (text, max = ANCHOR_QUOTE_MAX) => {
  const s = typeof text === 'string' ? text : '';
  return s.length > max ? `${s.slice(0, max).trimEnd()}…` : s;
};

// A line of the draft one may comment on: a sentence the draft added or changed (right pane)
export const isCommentableItem = (item) => item?.status === 'added' || item?.status === 'modified';

// The stacked comments on one sentence: [{ root, replies }] in the order given; pending ones (not submitted yet,
// visible only to their author) are flagged `pending`.
export const getThreads = (comments, anchorText, pending = []) => {
  const all = [...(comments || []), ...(pending || []).map((p) => ({ ...p, pending: true }))]
    .filter((c) => sameAnchor(c.anchor_text, anchorText));
  return all.filter((c) => !c.reply_to).map((root) => ({
    root, replies: all.filter((r) => r.reply_to && r.reply_to === root.feedback_id),
  }));
};

// The owner's editor: the threads whose sentence is no longer in the text being edited (listed at the top)
export const getOrphanThreads = (comments, markdown) => {
  const text = canonicalText(markdown);
  const roots = (comments || []).filter((c) => !c.reply_to);
  return roots.filter((root) => !text.includes(canonicalText(root.anchor_text))).map((root) => ({
    root, replies: (comments || []).filter((r) => r.reply_to === root.feedback_id),
  }));
};

// The threads whose sentence is still in the text, grouped per sentence in the text's order: [{ anchor, threads }]
export const getAnchoredThreads = (comments, markdown) => {
  const text = canonicalText(markdown);
  const anchors = [];
  (comments || []).filter((c) => !c.reply_to).forEach((root) => {
    const key = canonicalText(root.anchor_text);
    if (key && text.includes(key) && !anchors.some((a) => canonicalText(a) === key)) anchors.push(root.anchor_text);
  });
  return anchors
    .sort((a, b) => text.indexOf(canonicalText(a)) - text.indexOf(canonicalText(b)))
    .map((anchor) => ({ anchor, threads: getThreads(comments, anchor) }));
};

// ── A comment follows its edited sentence (Save as Draft) ────────────────────
// The sentences of an editor line's text (ends at . ! or ?, the last one may have none)
export const splitSentences = (text) => (String(text || '').replace(/\s+/g, ' ').match(/[^.!?]+(?:[.!?]+|$)/g) || [])
  .map((s) => s.trim()).filter(Boolean);
const wordsOf = (text) => new Set(canonicalText(text).split(/[^\p{L}\p{N}]+/u).filter(Boolean));
// How alike two sentences are: the share of the shorter one's words the other has (the smart-diff's own measure)
const overlap = (a, b) => {
  const x = wordsOf(a);
  const y = wordsOf(b);
  if (!x.size || !y.size) return 0;
  return [...x].filter((w) => y.has(w)).length / Math.min(x.size, y.size);
};
// The sentence a commented sentence became, from the text of the line it was on: the line's only sentence, else the
// one most like the old sentence (the first on a tie). '' when the line is empty or still has the old sentence.
export const getEditedSentence = (lineText, anchorText) => {
  const sentences = splitSentences(lineText);
  if (!sentences.length || canonicalText(lineText).includes(canonicalText(anchorText))) return '';
  if (sentences.length === 1) return sentences[0];
  return sentences.reduce((best, s) => (overlap(s, anchorText) > overlap(best, anchorText) ? s : best));
};
// What a Save as Draft sends for the comments whose line was edited (kept, its text changed): each thread's comment
// with the sentence it is on now. edited: [{ anchor, lineText, threads }] (a deleted or emptied line is not in it)
export const getAnchorUpdates = (edited) => (edited || []).flatMap(({ anchor, lineText, threads }) => {
  const sentence = getEditedSentence(lineText, anchor);
  return sentence ? (threads || []).map(({ root }) => ({ feedback_id: root.feedback_id, anchor_text: sentence })) : [];
});

// Who has given (still open) comments, by name, for the owner's draft bar
export const getCommentAuthors = (comments) => [...new Set((comments || [])
  .map((c) => c.author_name || c.author_email).filter(Boolean))];

const normEmail = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');
// The unsubmitted comments with one more, or null once MAX_PENDING_COMMENTS are held (the server's limit)
export const withPendingComment = (pending, comment) => ((pending || []).length >= MAX_PENDING_COMMENTS
  ? null : [...(pending || []), comment]);

export const isOwnComment = (comment, viewerEmail) => Boolean(normEmail(viewerEmail))
  && normEmail(comment?.author_email) === normEmail(viewerEmail);

// A refused comment action (an axios error): { message, reload }. The draft changed, was sent or the share ended:
// the page reloads.
const FEEDBACK_FALLBACK = 'The comment could not be saved. Please try again.';
const FEEDBACK_RELOAD_REASONS = new Set(['NO_PRIVATE_DRAFT', 'NOT_SHARED', 'FEEDBACK_NOT_FOUND', 'NOT_HOLDER',
  'DRAFT_PRIVATE', OUT_OF_SCOPE_REASON, 'NOT_FOUND']);
export const getFeedbackError = (error) => {
  const data = error?.response?.data || {};
  return {
    message: data.reason === OUT_OF_SCOPE_REASON ? OUT_OF_SCOPE_MESSAGE : data.message || FEEDBACK_FALLBACK,
    reload: FEEDBACK_RELOAD_REASONS.has(data.reason),
  };
};
